import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { BatchUploadResponse, Doc, PresignedSlot, PresignedUploadsResponse } from '../../types';

export interface BatchUploadOptions {
  // Per-file overrides parallel to `files`. Both default to the filename
  // (server applies that default when the label is empty), so callers can
  // omit one or both. When provided the array length must match `files`.
  labels?: string[];
  descriptions?: string[];
}

// The shared apiClient defaults Content-Type to application/json. With
// FormData payloads axios 1.x sees the JSON content type and serializes
// FormData via formDataToJSON, which silently strips the File objects —
// the server then sees an empty req.files / req.file and rejects with
// batch_empty. Forcing Content-Type to multipart/form-data here tells
// axios to keep the FormData intact; the XHR adapter then lets the
// browser set the real boundary header.
const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' };

/**
 * Service for document-related API calls
 */
export const documentService = {
  /**
   * Get documents for a device
   * @param deviceId - The device ID
   * @param from - Optional date to filter documents from
   */
  getDocuments: (deviceId: string, from?: Date): Promise<AxiosResponse<Doc[]>> => {
    const params = from ? { from } : {};
    return apiClient.get(`/api/devices/${deviceId}/documents`, { params });
  },

  /**
   * Current storage usage and limit for the CoC. Used to drive the
   * Documents sidebar quota indicator.
   */
  getDocumentsQuota: (
    deviceId: string,
  ): Promise<AxiosResponse<{ usedBytes: number; limitBytes: number }>> => {
    return apiClient.get(`/api/devices/${deviceId}/documents/quota`);
  },

  /**
   * Upload a new document
   * @param deviceId - The device ID
   * @param document - The document metadata
   * @param file - The file to upload
   */
  uploadDocument: (deviceId: string, document: Doc, file: File): Promise<AxiosResponse<Doc>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.set('label', document.label);
    formData.set('description', document.description);

    return apiClient.post(`/api/devices/${deviceId}/documents`, formData, {
      headers: MULTIPART_HEADERS,
    });
  },

  /**
   * Upload up to 20 documents to a device's batch endpoint.
   * Returns the raw 207 Multi-Status payload — callers decide how to
   * present per-file rejected/duplicate entries.
   */
  uploadDocuments: (
    deviceId: string,
    files: File[],
    opts: BatchUploadOptions = {},
  ): Promise<AxiosResponse<BatchUploadResponse>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    files.forEach((_, i) => {
      formData.append('labels', opts.labels?.[i] ?? '');
      formData.append('descriptions', opts.descriptions?.[i] ?? '');
    });
    return apiClient.post(`/api/devices/${deviceId}/documents/batch`, formData, {
      headers: MULTIPART_HEADERS,
    });
  },

  /**
   * Delete a document. Server S3-deletes then Mongo-deletes; 204 on success.
   */
  deleteDocument: (deviceId: string, documentId: string): Promise<AxiosResponse> => {
    return apiClient.delete(`/api/devices/${deviceId}/documents/${documentId}`);
  },

  /**
   * Request presigned PUT URLs for one or more files. The server reserves
   * quota and returns a slot per file; the client uploads directly to S3
   * with `uploadToSlot`, then completes the flow with `finalizeUpload`.
   */
  requestPresignedUploads: (
    deviceId: string,
    files: { filename: string; mimeType: string; sizeBytes: number; sha256?: string }[],
  ): Promise<AxiosResponse<PresignedUploadsResponse>> => {
    return apiClient.post(`/api/devices/${deviceId}/documents/uploads`, { files });
  },

  /**
   * Upload a single file straight to S3 via a presigned URL. Bypasses the
   * shared apiClient on purpose — S3 must not see the Auth0 bearer, no
   * cache invalidation should fire, and the request needs raw progress
   * events from XHR.
   */
  uploadToSlot: (
    slot: PresignedSlot,
    file: File,
    onProgress?: (loadedBytes: number, totalBytes: number) => void,
  ): Promise<void> => {
    return axios
      .request({
        url: slot.url,
        method: slot.method,
        data: file,
        headers: slot.headers,
        // Don't let axios mutate the binary payload (e.g. by trying to
        // JSON-stringify the File).
        transformRequest: [(d) => d],
        onUploadProgress: (evt) => {
          if (!onProgress) return;
          const total = evt.total || file.size;
          onProgress(evt.loaded, total);
        },
      })
      .then(() => undefined);
  },

  /**
   * Mark a presigned slot complete. The server verifies the S3 object
   * exists, creates the Document row, fires push notifications, and
   * returns the new Document.
   */
  finalizeUpload: (
    deviceId: string,
    slotId: string,
    body: { label?: string; description?: string },
  ): Promise<AxiosResponse<Doc>> => {
    return apiClient.post(
      `/api/devices/${deviceId}/documents/uploads/${slotId}/finalize`,
      body,
    );
  },
};
